import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Check, X } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * ProposedLinkPopover — a link the AI drew, for the room to keep or drop.
 *
 * A proposed connection (metadata.status 'proposed': a town goal leading to a
 * shared target, drawn by the target round) is dashed on the scene. Tapping it
 * opens this at the tap point: what leads to what, the AI's one-line reason,
 * and the two decisions. Accept makes it a claim of the room; reject takes it
 * off the map and keeps the row (specs/tki-kuutio-flow.md). Same shell as
 * LinkKindPopover, so the two read as one family.
 */

const appear = keyframes`
  from { opacity: 0; transform: translate(-8px, -50%) scale(0.96); }
  to   { opacity: 1; transform: translate(0, -50%) scale(1); }
`;

const Catcher = styled.div`
  position: fixed; inset: 0; z-index: 1200; background: transparent;
`;

const Card = styled.div`
  position: fixed; z-index: 1201; transform: translateY(-50%);
  animation: ${appear} 0.14s cubic-bezier(0.2, 0.9, 0.3, 1);
  padding: 12px; border-radius: 14px; background: #14141f;
  border: 1px solid rgba(255, 255, 255, 0.13); box-shadow: 0 14px 44px rgba(0, 0, 0, 0.6);
  display: flex; flex-direction: column; gap: 10px; width: 320px; color: #fff;
  .title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(255,255,255,0.55); }
  .ends { display: flex; flex-direction: column; gap: 4px; font-size: 0.86rem; line-height: 1.35; }
  .ends .arrow { color: rgba(255,255,255,0.45); font-size: 0.78rem; }
  .reason { font-size: 0.78rem; line-height: 1.4; color: rgba(255,255,255,0.6); border-left: 2px solid rgba(255,255,255,0.18); padding-left: 8px; }
  .actions { display: flex; gap: 8px; }
`;

const Decide = styled.button`
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 10px; border-radius: 10px; font: inherit; font-size: 0.86rem; font-weight: 600; cursor: pointer;
  color: ${(p) => p.$color}; background: transparent; border: 1.5px solid ${(p) => p.$color};
  &:hover { background: ${(p) => p.$color}22; }
`;

export default function ProposedLinkPopover({ anchor, link, onDecide, onClose }) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const margin = 12;
    const gap = 24;
    const half = rect.height / 2;
    const fitsRight = anchor.x + gap + rect.width <= window.innerWidth - margin;
    const left = fitsRight ? anchor.x + gap : Math.max(margin, anchor.x - gap - rect.width);
    const top = Math.min(Math.max(anchor.y, half + margin), window.innerHeight - half - margin);
    setPlacement({ left, top });
  }, [anchor]);

  if (!anchor || !link) return null;

  return (
    <>
      <Catcher onClick={onClose} />
      <Card ref={cardRef} style={{ left: placement.left, top: placement.top }} onClick={(e) => e.stopPropagation()}>
        <div className="title">{t('impactPath.proposedLink.title')}</div>
        <div className="ends">
          <span>{link.fromText}</span>
          <span className="arrow">↓ {link.effectType ? t(`impactPath.scene.edge.${link.effectType}`) : t('impactPath.scene.edge.leads_to')}</span>
          <span>{link.toText}</span>
        </div>
        {link.reason && <div className="reason">{t('impactPath.proposedLink.by')}: {link.reason}</div>}
        <div className="actions">
          <Decide type="button" $color="#4caf50" onClick={() => onDecide?.('accept')}><Check size={14} />{t('impactPath.proposedLink.accept')}</Decide>
          <Decide type="button" $color="#EF5350" onClick={() => onDecide?.('reject')}><X size={14} />{t('impactPath.proposedLink.reject')}</Decide>
        </div>
      </Card>
    </>
  );
}
